import * as React from 'react'
import * as ContextMenuPrimitive from '@radix-ui/react-context-menu'
import { Check, CaretRight } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * ContextMenu — a right-click / long-press action menu (Radix-backed), the
 * cursor-anchored sibling of `DropdownMenu`. Wrap the area a user should be able
 * to right-click in a `ContextMenuTrigger` and list *actions* related to that
 * item (rename, delete, copy…) — not form values.
 *
 *   <ContextMenu>
 *     <ContextMenuTrigger>
 *       <div className="…">Right-click me</div>
 *     </ContextMenuTrigger>
 *     <ContextMenuContent>
 *       <ContextMenuItem icon={<Copy />} shortcut="⌘C">Copy</ContextMenuItem>
 *       <ContextMenuSeparator />
 *       <ContextMenuSub>
 *         <ContextMenuSubTrigger>Share</ContextMenuSubTrigger>
 *         <ContextMenuSubContent>
 *           <ContextMenuItem>Copy link</ContextMenuItem>
 *         </ContextMenuSubContent>
 *       </ContextMenuSub>
 *       <ContextMenuItem destructive icon={<Trash />}>Delete</ContextMenuItem>
 *     </ContextMenuContent>
 *   </ContextMenu>
 *
 * Shares the `DropdownMenu` visual language exactly (white panel, gray-200
 * border, `shadow-fbui-lg`; 14px gray-700 rows with a gray-100 highlight fill;
 * gray-500 leading icons; 12px tertiary shortcut hints) and the same rich item
 * API (`icon`, `shortcut`, `description`, `destructive`, `inset`, and the `size`
 * that flows to items via context). Radix supplies right-click / long-press
 * opening, focus trapping, arrow-key + typeahead navigation, and focus return.
 *
 * Item variants:
 *  - `ContextMenuCheckboxItem` / `ContextMenuRadioItem` — leading ✓ indicator
 *  - `ContextMenuItem destructive` — red "Delete"-style action
 *  - `ContextMenuItem description=` — two-line item (label + subtitle)
 *  - `ContextMenuItem inset` / `ContextMenuLabel inset` — indent an icon-less row
 *    so its label lines up with sibling rows that have a leading icon
 *  - `ContextMenuSub` / `SubTrigger` (trailing caret) / `SubContent` — submenus
 *
 * `ContextMenuContent size="sm"` compacts every item (rows/text/glyphs); the size
 * flows to items (and submenus) via context. A long menu caps at the collision-
 * available height and scrolls automatically.
 *
 * Icon rule: within one menu surface it's all icons or none — don't mix. A lone
 * icon-less row between icon'd rows reads as ragged. `inset` exists to align a
 * `ContextMenuLabel` (or a stray row) inside a menu whose interactive rows carry
 * a leading indicator, not to license a mixed icon/no-icon action list.
 */

export const ContextMenu = ContextMenuPrimitive.Root
export const ContextMenuTrigger = ContextMenuPrimitive.Trigger
export const ContextMenuGroup = ContextMenuPrimitive.Group
export const ContextMenuPortal = ContextMenuPrimitive.Portal

export interface ContextMenuContentProps extends React.ComponentPropsWithoutRef<
  typeof ContextMenuPrimitive.Content
> {
  /** Compact menu (smaller rows/text) to pair with a dense surface. */
  size?: ContextMenuSize
}

export const ContextMenuContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Content>,
  ContextMenuContentProps
>(({ className, size = 'md', children, ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.Content
      ref={ref}
      className={cn(
        // A long menu caps at the available height to the collision edge and
        // scrolls (Radix sets the var), so it never runs off the viewport.
        'fbui-scroll z-50 min-w-48 max-h-[var(--radix-context-menu-content-available-height)] overflow-y-auto rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg',
        'origin-[--radix-context-menu-content-transform-origin]',
        className
      )}
      {...props}
    >
      <ContextMenuSizeContext.Provider value={size}>
        {children}
      </ContextMenuSizeContext.Provider>
    </ContextMenuPrimitive.Content>
  </ContextMenuPrimitive.Portal>
))
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName

type ContextMenuSize = 'sm' | 'md'

/** Published by `ContextMenuContent` so every item type matches the menu size. */
const ContextMenuSizeContext = React.createContext<ContextMenuSize>('md')
const useMenuSize = () => React.useContext(ContextMenuSizeContext)

/** Shared row layout + highlight/disabled states for every menu item type. */
const itemBase =
  'group flex cursor-pointer items-center rounded-lg text-fbui-gray-700 outline-none select-none data-[highlighted]:bg-fbui-gray-100 data-[highlighted]:text-fbui-gray-900 data-[disabled]:pointer-events-none data-[disabled]:text-fbui-gray-300'

/** Per-size row spacing + text. */
const itemSize: Record<ContextMenuSize, string> = {
  sm: 'gap-2 px-2.5 py-1.5 text-xs',
  md: 'gap-2.5 px-3 py-2 text-sm',
}

/** Per-size icon / indicator glyph size. */
const glyphSize: Record<ContextMenuSize, string> = {
  sm: 'size-3.5',
  md: 'size-4',
}

/**
 * Empty leading slot that reserves the icon's width so an icon-less row's label
 * lines up with sibling rows that have a leading icon (the `inset` treatment).
 */
const InsetSpacer = ({ size }: { size: ContextMenuSize }) => (
  <span aria-hidden className={cn('shrink-0', glyphSize[size])} />
)

/** Destructive override — red text + red-50 highlight (the "Delete" action). */
const itemDestructive =
  'text-fbui-red-700 data-[highlighted]:bg-fbui-red-50 data-[highlighted]:text-fbui-red-700'

/** Trailing keyboard-shortcut hint. */
export const ContextMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn('ml-auto pl-4 text-xs text-fbui-gray-400', className)}
    {...props}
  />
)
ContextMenuShortcut.displayName = 'ContextMenuShortcut'

export interface ContextMenuItemProps extends React.ComponentPropsWithoutRef<
  typeof ContextMenuPrimitive.Item
> {
  /** Leading icon (sized to 16px, gray). */
  icon?: React.ReactNode
  /** Trailing keyboard-shortcut hint (e.g. "⌘K"). */
  shortcut?: React.ReactNode
  /** Secondary subtitle line below the label (two-line item). */
  description?: React.ReactNode
  /** Red destructive treatment (e.g. "Delete"). */
  destructive?: boolean
  /**
   * Reserve the leading icon slot on a row that has no `icon`, so it aligns with
   * indicator rows in the same menu (e.g. a plain action alongside checkbox/radio
   * items). No-op when `icon` is set. Not a license to mix icon'd and icon-less
   * *actions* — keep an action menu all-icons or none (see the component doc).
   */
  inset?: boolean
}

export const ContextMenuItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Item>,
  ContextMenuItemProps
>(
  (
    {
      className,
      icon,
      shortcut,
      description,
      destructive,
      inset,
      children,
      ...props
    },
    ref
  ) => {
    const size = useMenuSize()
    return (
      <ContextMenuPrimitive.Item
        ref={ref}
        className={cn(
          itemBase,
          itemSize[size],
          '[&>svg]:shrink-0',
          size === 'sm' ? '[&>svg]:size-3.5' : '[&>svg]:size-4',
          // Top-align the icon with the first line when there's a subtitle.
          description && 'items-start',
          destructive && itemDestructive,
          className
        )}
        {...props}
      >
        {icon ? (
          <span
            className={cn(
              'flex shrink-0 items-center justify-center group-data-[disabled]:text-fbui-gray-300',
              glyphSize[size],
              size === 'sm' ? '[&>svg]:size-3.5' : '[&>svg]:size-4',
              description && 'mt-0.5',
              destructive ? 'text-fbui-red-700' : 'text-fbui-gray-500'
            )}
          >
            {icon}
          </span>
        ) : inset ? (
          <InsetSpacer size={size} />
        ) : null}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{children}</span>
          {description && (
            <span
              className={cn(
                'truncate text-xs font-normal group-data-[disabled]:text-fbui-gray-300',
                destructive
                  ? 'text-fbui-red-500'
                  : 'text-fbui-gray-500 group-data-[highlighted]:text-fbui-gray-600'
              )}
            >
              {description}
            </span>
          )}
        </span>
        {shortcut && <ContextMenuShortcut>{shortcut}</ContextMenuShortcut>}
      </ContextMenuPrimitive.Item>
    )
  }
)
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName

export interface ContextMenuCheckboxItemProps extends React.ComponentPropsWithoutRef<
  typeof ContextMenuPrimitive.CheckboxItem
> {
  /** Trailing keyboard-shortcut hint. */
  shortcut?: React.ReactNode
  /**
   * Close the menu when toggled. Defaults to `false` so the menu stays open and
   * you can see the result of the toggle (and flip several in a row).
   */
  closeOnSelect?: boolean
}

/** Toggleable item with a leading ✓ when checked. */
export const ContextMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.CheckboxItem>,
  ContextMenuCheckboxItemProps
>(
  (
    {
      className,
      shortcut,
      children,
      onSelect,
      closeOnSelect = false,
      ...props
    },
    ref
  ) => {
    const size = useMenuSize()
    return (
      <ContextMenuPrimitive.CheckboxItem
        ref={ref}
        className={cn(itemBase, itemSize[size], className)}
        onSelect={(event) => {
          onSelect?.(event)
          if (!closeOnSelect) event.preventDefault()
        }}
        {...props}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center',
            glyphSize[size]
          )}
        >
          <ContextMenuPrimitive.ItemIndicator>
            <Check
              weight="bold"
              className={glyphSize[size]}
              aria-hidden="true"
            />
          </ContextMenuPrimitive.ItemIndicator>
        </span>
        <span className="flex-1 truncate">{children}</span>
        {shortcut && <ContextMenuShortcut>{shortcut}</ContextMenuShortcut>}
      </ContextMenuPrimitive.CheckboxItem>
    )
  }
)
ContextMenuCheckboxItem.displayName =
  ContextMenuPrimitive.CheckboxItem.displayName

export const ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup

export interface ContextMenuRadioItemProps extends React.ComponentPropsWithoutRef<
  typeof ContextMenuPrimitive.RadioItem
> {
  /** Trailing keyboard-shortcut hint. */
  shortcut?: React.ReactNode
  /**
   * Close the menu when selected. Defaults to `false` so the menu stays open and
   * you can see which option is now selected.
   */
  closeOnSelect?: boolean
}

/** Single-choice item (inside a RadioGroup) with a leading ✓ when selected. */
export const ContextMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.RadioItem>,
  ContextMenuRadioItemProps
>(
  (
    {
      className,
      shortcut,
      children,
      onSelect,
      closeOnSelect = false,
      ...props
    },
    ref
  ) => {
    const size = useMenuSize()
    return (
      <ContextMenuPrimitive.RadioItem
        ref={ref}
        className={cn(itemBase, itemSize[size], className)}
        onSelect={(event) => {
          onSelect?.(event)
          if (!closeOnSelect) event.preventDefault()
        }}
        {...props}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center',
            glyphSize[size]
          )}
        >
          <ContextMenuPrimitive.ItemIndicator>
            <Check
              weight="bold"
              className={glyphSize[size]}
              aria-hidden="true"
            />
          </ContextMenuPrimitive.ItemIndicator>
        </span>
        <span className="flex-1 truncate">{children}</span>
        {shortcut && <ContextMenuShortcut>{shortcut}</ContextMenuShortcut>}
      </ContextMenuPrimitive.RadioItem>
    )
  }
)
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName

export interface ContextMenuLabelProps extends React.ComponentPropsWithoutRef<
  typeof ContextMenuPrimitive.Label
> {
  /** Indent to align with items that have a leading icon. */
  inset?: boolean
}

export const ContextMenuLabel = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Label>,
  ContextMenuLabelProps
>(({ className, inset, children, ...props }, ref) => {
  const size = useMenuSize()
  return (
    <ContextMenuPrimitive.Label
      ref={ref}
      className={cn(
        'flex items-center px-3 py-1.5 text-xs font-medium text-fbui-gray-500',
        size === 'sm' ? 'gap-2' : 'gap-2.5',
        className
      )}
      {...props}
    >
      {inset && <InsetSpacer size={size} />}
      <span className="truncate">{children}</span>
    </ContextMenuPrimitive.Label>
  )
})
ContextMenuLabel.displayName = ContextMenuPrimitive.Label.displayName

export const ContextMenuSeparator = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1.5 my-1.5 h-px bg-fbui-gray-200', className)}
    {...props}
  />
))
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName

export const ContextMenuSub = ContextMenuPrimitive.Sub

export interface ContextMenuSubTriggerProps extends React.ComponentPropsWithoutRef<
  typeof ContextMenuPrimitive.SubTrigger
> {
  /** Leading icon (sized to 16px, gray). */
  icon?: React.ReactNode
  /** Indent an icon-less submenu trigger to align with icon'd sibling rows. */
  inset?: boolean
}

/** Item that opens a submenu — highlights while open, trailing caret. */
export const ContextMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubTrigger>,
  ContextMenuSubTriggerProps
>(({ className, icon, inset, children, ...props }, ref) => {
  const size = useMenuSize()
  return (
    <ContextMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(
        itemBase,
        itemSize[size],
        'data-[state=open]:bg-fbui-gray-100',
        className
      )}
      {...props}
    >
      {icon ? (
        <span
          className={cn(
            'flex shrink-0 items-center justify-center text-fbui-gray-500 group-data-[disabled]:text-fbui-gray-300',
            glyphSize[size],
            size === 'sm' ? '[&>svg]:size-3.5' : '[&>svg]:size-4'
          )}
        >
          {icon}
        </span>
      ) : inset ? (
        <InsetSpacer size={size} />
      ) : null}
      <span className="flex-1 truncate">{children}</span>
      <CaretRight
        weight="bold"
        aria-hidden="true"
        className="ml-auto size-3.5 shrink-0 text-fbui-gray-500"
      />
    </ContextMenuPrimitive.SubTrigger>
  )
})
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName

export const ContextMenuSubContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.SubContent
      ref={ref}
      className={cn(
        'fbui-scroll z-50 min-w-44 max-h-[var(--radix-context-menu-content-available-height)] overflow-y-auto rounded-xl border border-fbui-gray-200 bg-fbui-base-white p-1.5 shadow-fbui-lg',
        'origin-[--radix-context-menu-content-transform-origin]',
        className
      )}
      {...props}
    />
  </ContextMenuPrimitive.Portal>
))
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName
